import { Icon } from "@iconify/react";
import { Tooltip as ReactTooltip } from "react-tooltip";
import { getTooltipClassName } from "../../../../utils";
import { QuotationReferenceTag } from "./QuotationReferenceTag";

interface HeaderProps {
  isDarkMode: boolean;
  isSidebarOpen: boolean;
  setIsSidebarOpen: (open: boolean) => void;
  toggleDarkMode: () => void;
  selectedSessionName: string | null;
  selectedSessionId: string | null;
  onDeleteClick: () => void;
  onRenameClick: () => void;
}

export function Header({
  isDarkMode,
  isSidebarOpen,
  selectedSessionName,
  selectedSessionId,
  setIsSidebarOpen,
  toggleDarkMode,
  onDeleteClick,
  onRenameClick,
}: HeaderProps) {
  return (
    <header
      className={[
        "Header",
        "grid",
        "grid-cols-[auto_1fr_auto]",
        "items-center",
        "gap-2",
        "md:gap-4",
        "p-2",
        "md:p-4",
        "border-b",
        "min-w-0",
        isDarkMode ? "border-gray-700 bg-gray-800" : "border-gray-200 bg-white",
      ].join(" ")}
    >
      {!isSidebarOpen ? (
        <button
          type="button"
          onClick={() => setIsSidebarOpen(true)}
          className={[
            "w-8",
            "h-8",
            "rounded",
            "grid",
            "place-items-center",
            "transition-colors",
            isDarkMode ? "bg-gray-700 hover:bg-gray-600" : "bg-gray-100 hover:bg-gray-200",
          ].join(" ")}
          aria-label="Open sidebar"
        >
          <Icon
            icon="mdi:menu"
            className={`w-5 h-5 ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}
          />
        </button>
      ) : (
        <div />
      )}
      <h5 className={`font-semibold truncate ${isDarkMode ? "text-gray-100!" : "text-gray-900!"}`}>
        <span className="hidden md:inline!">
          Quotation Request Chat{!!selectedSessionName && " - "}
        </span>
        {selectedSessionName ? selectedSessionName : ""}
        <QuotationReferenceTag selectedSessionId={selectedSessionId} isDarkMode={isDarkMode} />
      </h5>
      <div className="grid grid-flow-col gap-1 md:gap-2">
        {selectedSessionName && (
          <>
            <button
              type="button"
              onClick={onRenameClick}
              className={[
                "w-8",
                "h-8",
                "rounded-lg!",
                "grid",
                "place-items-center",
                "transition-colors",
                "rename-session-btn",
                isDarkMode ? "bg-blue-600 hover:bg-blue-700" : "bg-blue-200 hover:bg-blue-300",
              ].join(" ")}
              aria-label="Rename session"
            >
              <Icon
                icon="mdi:pencil"
                className={`w-5 h-5 ${isDarkMode ? "text-white" : "text-blue-700"}`}
              />
            </button>
            <ReactTooltip
              anchorSelect=".rename-session-btn"
              place="top"
              content="Rename session"
              className={getTooltipClassName(isDarkMode)}
            />
            <button
              type="button"
              onClick={onDeleteClick}
              className={[
                "w-8",
                "h-8",
                "rounded-lg!",
                "grid",
                "place-items-center",
                "transition-colors",
                "delete-session-btn",
                "bg-red-500",
                "hover:bg-red-600",
              ].join(" ")}
              aria-label="Delete session"
            >
              <Icon icon="mdi:delete" className="w-5 h-5 text-white" />
            </button>
            <ReactTooltip
              anchorSelect=".delete-session-btn"
              place="top"
              content="Delete session"
              className={getTooltipClassName(isDarkMode)}
            />
          </>
        )}
        <button
          type="button"
          onClick={toggleDarkMode}
          className={[
            "w-8",
            "h-8",
            "rounded-lg!",
            "grid",
            "place-items-center",
            "transition-colors",
            "toggle-dark-mode-btn",
            isDarkMode ? "bg-gray-700 hover:bg-gray-600" : "bg-gray-100 hover:bg-gray-200",
          ].join(" ")}
          aria-label="Toggle dark mode"
        >
          {isDarkMode ? (
            <Icon icon="mdi:weather-sunny" className="w-5 h-5 text-yellow-500" />
          ) : (
            <Icon
              icon="mdi:weather-night"
              className={`w-5 h-5 ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}
            />
          )}
        </button>
        <ReactTooltip
          anchorSelect=".toggle-dark-mode-btn"
          place="top"
          content={isDarkMode ? "Switch to light mode" : "Switch to dark mode"}
          className={getTooltipClassName(isDarkMode)}
        />
      </div>
    </header>
  );
}
